import { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Plus, Trash2, Loader2, Activity, TrendingDown, TrendingUp, Sparkles, Download } from "lucide-react";

const KG_TO_LB = 2.2046226218;
const METRICS = [
  { id: "weight", label: "Weight", key: "weight_kg", mass: true },
  { id: "bodyfat", label: "Body Fat %", key: "body_fat_pct", suffix: "%" },
  { id: "visceral", label: "Visceral Fat", key: "visceral_fat" },
  { id: "lbm", label: "LBM", key: "lbm_kg", mass: true },
  { id: "water", label: "Water %", key: "water_pct", suffix: "%" },
];

const fmtMass = (kg, unit) => (kg == null ? "—" : unit === "lb" ? `${(kg * KG_TO_LB).toFixed(1)} lb` : `${kg.toFixed(1)} kg`);
const fmtVal = (v, suffix) => (v == null ? "—" : `${v}${suffix || ""}`);

function buildSummary(readings, unit) {
  if (!readings || readings.length < 2) return null;
  const massUnit = unit === "lb" ? "lb" : "kg";
  const toUnit = (kg) => (unit === "lb" ? kg * KG_TO_LB : kg);
  const pair = (key) => {
    const withVal = readings.filter((r) => r[key] != null);
    return withVal.length >= 2 ? [withVal[0], withVal[withVal.length - 1]] : null;
  };
  const items = [];
  const bf = pair("body_fat_pct");
  if (bf) {
    const [f, l] = bf;
    if (f.weight_kg != null && l.weight_kg != null) {
      const d = toUnit((l.weight_kg * l.body_fat_pct) / 100 - (f.weight_kg * f.body_fat_pct) / 100);
      items.push({ id: "fatmass", label: `${Math.abs(d).toFixed(1)} ${massUnit} body fat`, delta: d, goodWhen: "down" });
    } else {
      const d = l.body_fat_pct - f.body_fat_pct;
      items.push({ id: "fatpct", label: `${Math.abs(d).toFixed(1)}% body fat`, delta: d, goodWhen: "down" });
    }
  }
  const lbm = pair("lbm_kg");
  if (lbm) {
    const d = toUnit(lbm[1].lbm_kg - lbm[0].lbm_kg);
    items.push({ id: "lbm", label: `${Math.abs(d).toFixed(1)} ${massUnit} lean mass`, delta: d, goodWhen: "up" });
  }
  const w = pair("weight_kg");
  if (w) {
    const d = toUnit(w[1].weight_kg - w[0].weight_kg);
    items.push({ id: "weight", label: `${Math.abs(d).toFixed(1)} ${massUnit} weight`, delta: d, goodWhen: "neutral" });
  }
  const vf = pair("visceral_fat");
  if (vf) {
    const d = vf[1].visceral_fat - vf[0].visceral_fat;
    items.push({ id: "visceral", label: `${Math.abs(d).toFixed(1)} visceral fat`, delta: d, goodWhen: "down" });
  }
  const wa = pair("water_pct");
  if (wa) {
    const d = wa[1].water_pct - wa[0].water_pct;
    items.push({ id: "water", label: `${Math.abs(d).toFixed(1)}% water`, delta: d, goodWhen: "up" });
  }
  const firstDate = readings[0].date;
  return { firstDate, items: items.filter((i) => Math.abs(i.delta) >= 0.05) };
}

function drawProgressCard(summary, memberName) {
  const W = 1080, H = 1080;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  const rr = (x, y, w, h, r) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  };
  // background
  ctx.fillStyle = "#16324f";
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = "rgba(13,148,136,0.18)";
  ctx.beginPath(); ctx.arc(W - 80, -40, 340, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(60, H - 20, 260, 0, Math.PI * 2); ctx.fill();
  // brand
  ctx.fillStyle = "#2dd4bf";
  ctx.font = "600 34px Arial";
  ctx.textAlign = "center";
  ctx.fillText("MY BEST BODY CLUB™", W / 2, 120);
  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 76px Georgia";
  ctx.fillText(memberName ? `${memberName.split(" ")[0]}'s Progress` : "My Progress", W / 2, 230);
  ctx.fillStyle = "rgba(255,255,255,0.65)";
  ctx.font = "30px Arial";
  ctx.fillText(`Since my first BIA reading · ${summary.firstDate}`, W / 2, 290);
  // chips
  ctx.font = "600 34px Arial";
  const chipH = 84, gapX = 22, gapY = 26, padX = 40, maxW = W - 140;
  const chips = summary.items.map((i) => {
    const down = i.delta < 0;
    const good = i.goodWhen === "neutral" ? null : (i.goodWhen === "down") === down;
    return { text: `${down ? "▼" : "▲"}  ${i.label}`, good };
  });
  const rows = [];
  let row = [], rowW = 0;
  for (const c of chips) {
    const w = ctx.measureText(c.text).width + padX * 2;
    if (rowW + w + (row.length ? gapX : 0) > maxW && row.length) { rows.push(row); row = []; rowW = 0; }
    row.push({ ...c, w });
    rowW += w + (row.length > 1 ? gapX : 0);
  }
  if (row.length) rows.push(row);
  let y = 400;
  for (const r of rows) {
    const totalW = r.reduce((s, c) => s + c.w, 0) + gapX * (r.length - 1);
    let x = (W - totalW) / 2;
    for (const c of r) {
      ctx.fillStyle = c.good === null ? "rgba(255,255,255,0.14)" : c.good ? "#0d9488" : "#b45309";
      rr(x, y, c.w, chipH, chipH / 2);
      ctx.fill();
      ctx.fillStyle = "#ffffff";
      ctx.fillText(c.text, x + c.w / 2, y + chipH / 2 + 12);
      x += c.w + gapX;
    }
    y += chipH + gapY;
  }
  // footer
  ctx.fillStyle = "rgba(255,255,255,0.55)";
  ctx.font = "28px Arial";
  ctx.fillText("Data-driven nutrition · Body composition over guesswork", W / 2, H - 130);
  ctx.fillStyle = "#2dd4bf";
  ctx.font = "600 32px Arial";
  ctx.fillText("mybestbodyclub.com", W / 2, H - 75);
  canvas.toBlob((blob) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "mbbc-progress-card.png";
    a.click();
    URL.revokeObjectURL(url);
  }, "image/png");
}

function ProgressSummary({ readings, unit, memberName }) {
  const summary = buildSummary(readings, unit);
  if (!summary || summary.items.length === 0) return null;
  return (
    <div className="bg-navy rounded-2xl p-6 text-white relative overflow-hidden" data-testid="progress-summary">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-teal" />
          <span className="text-xs uppercase tracking-[0.15em] font-semibold text-white/60">Since your first reading · {summary.firstDate}</span>
        </div>
        <button onClick={() => { drawProgressCard(summary, memberName); toast.success("Progress card downloaded!"); }} data-testid="download-progress-card"
          className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 transition-colors rounded-full px-4 py-2 text-xs font-medium">
          <Download className="w-3.5 h-3.5 text-teal" /> Download my progress card
        </button>
      </div>
      <div className="mt-4 flex flex-wrap gap-2.5">
        {summary.items.map((i) => {
          const down = i.delta < 0;
          const good = i.goodWhen === "neutral" ? null : (i.goodWhen === "down") === down;
          const tone = good === null ? "bg-white/10 text-white" : good ? "bg-teal/25 text-teal-100" : "bg-amber-400/20 text-amber-200";
          return (
            <span key={i.id} data-testid={`summary-${i.id}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium ${tone}`}>
              {down ? <TrendingDown className="w-4 h-4" /> : <TrendingUp className="w-4 h-4" />}
              {down ? "▼" : "▲"} {i.label}
            </span>
          );
        })}
      </div>
    </div>
  );
}

export const BiaTracker = ({ readingsPath, createPath, deletePathBase, memberName }) => {
  const [readings, setReadings] = useState(null);
  const [unit, setUnit] = useState(() => localStorage.getItem("mbbc_weight_unit") || "kg");
  const [metric, setMetric] = useState("weight");
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), weight: "", body_fat_pct: "", visceral_fat: "", lbm: "", water_pct: "" });

  const load = useCallback(async () => {
    try { const { data } = await api.get(readingsPath); setReadings(data); }
    catch { setReadings([]); toast.error("Could not load readings"); }
  }, [readingsPath]);
  useEffect(() => { load(); }, [load]);

  const setUnitPref = (u) => { setUnit(u); localStorage.setItem("mbbc_weight_unit", u); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { date: form.date, weight: parseFloat(form.weight), weight_unit: unit };
      for (const k of ["body_fat_pct", "visceral_fat", "water_pct"]) if (form[k] !== "") payload[k] = parseFloat(form[k]);
      if (form.lbm !== "") payload.lbm = parseFloat(form.lbm);
      await api.post(createPath, payload);
      toast.success("Reading saved!");
      setForm({ ...form, weight: "", body_fat_pct: "", visceral_fat: "", lbm: "", water_pct: "" });
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save reading");
    } finally { setBusy(false); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete the reading from ${r.date}?`)) return;
    try {
      await api.delete(`${deletePathBase}/${r.id}`);
      toast.success("Reading deleted");
      load();
    } catch { toast.error("Could not delete reading"); }
  };

  const activeMetric = METRICS.find((m) => m.id === metric);
  const chartData = useMemo(() => (readings || [])
    .filter((r) => r[activeMetric.key] != null)
    .map((r) => ({
      date: r.date,
      value: activeMetric.mass && unit === "lb" ? +(r[activeMetric.key] * KG_TO_LB).toFixed(1) : r[activeMetric.key],
    })), [readings, activeMetric, unit]);

  const latest = readings && readings.length ? readings[readings.length - 1] : null;
  const unitLabel = activeMetric.mass ? ` (${unit})` : activeMetric.suffix ? ` (${activeMetric.suffix})` : "";
  const inputCls = "w-full border border-[#E7E5E4] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal";

  if (readings === null) return <Loader2 className="w-6 h-6 animate-spin text-teal" />;

  return (
    <div className="space-y-6" data-testid="bia-tracker">
      {/* header row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-navy">
          <Activity className="w-5 h-5 text-teal" />
          <h2 className="font-heading text-2xl">BIA Progress Tracker</h2>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex rounded-full border border-[#E7E5E4] overflow-hidden text-xs font-medium">
            {["kg", "lb"].map((u) => (
              <button key={u} onClick={() => setUnitPref(u)} data-testid={`unit-${u}`}
                className={`px-3.5 py-1.5 ${unit === u ? "bg-navy text-white" : "bg-white text-[#57534E]"}`}>{u}</button>
            ))}
          </div>
          <button onClick={() => setShowForm(!showForm)} data-testid="tracker-add-btn"
            className="inline-flex items-center gap-1.5 bg-teal text-white rounded-full px-5 py-2 text-sm font-medium hover:bg-teal-dark">
            <Plus className="w-4 h-4" /> Add reading
          </button>
        </div>
      </div>

      <ProgressSummary readings={readings} unit={unit} memberName={memberName} />


      {/* latest stats */}
      {latest && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {METRICS.map((m) => (
            <div key={m.id} className="bg-white border border-[#E7E5E4] rounded-xl p-4" data-testid={`stat-${m.id}`}>
              <div className="text-[10px] uppercase tracking-wider text-[#78716C]">{m.label}</div>
              <div className="mt-1 font-heading text-xl text-navy">
                {m.mass ? fmtMass(latest[m.key], unit) : fmtVal(latest[m.key], m.suffix)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* add form */}
      {showForm && (
        <form onSubmit={submit} className="bg-white border border-[#E7E5E4] rounded-2xl p-6 shadow-sm" data-testid="tracker-form">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-medium text-navy">Date</label>
              <input data-testid="tracker-date" type="date" required value={form.date} onChange={set("date")} className={inputCls} />
            </div>
            <div>
              <label className="text-xs font-medium text-navy">Weight ({unit}) *</label>
              <input data-testid="tracker-weight" type="number" step="0.1" min="1" required placeholder={unit === "kg" ? "72.5" : "160"} value={form.weight} onChange={set("weight")} className={inputCls} />
            </div>
            <div>
              <label className="text-xs font-medium text-navy">Body fat %</label>
              <input data-testid="tracker-bodyfat" type="number" step="0.1" placeholder="30.2" value={form.body_fat_pct} onChange={set("body_fat_pct")} className={inputCls} />
            </div>
            <div>
              <label className="text-xs font-medium text-navy">Visceral fat</label>
              <input data-testid="tracker-visceral" type="number" step="0.5" placeholder="8" value={form.visceral_fat} onChange={set("visceral_fat")} className={inputCls} />
            </div>
            <div>
              <label className="text-xs font-medium text-navy">LBM ({unit})</label>
              <input data-testid="tracker-lbm" type="number" step="0.1" placeholder={unit === "kg" ? "48.3" : "106"} value={form.lbm} onChange={set("lbm")} className={inputCls} />
            </div>
            <div>
              <label className="text-xs font-medium text-navy">Water %</label>
              <input data-testid="tracker-water" type="number" step="0.1" placeholder="52.4" value={form.water_pct} onChange={set("water_pct")} className={inputCls} />
            </div>
          </div>
          <button type="submit" disabled={busy} data-testid="tracker-submit"
            className="mt-5 w-full sm:w-auto bg-navy text-white rounded-full px-8 py-2.5 text-sm font-medium hover:bg-navy-dark flex items-center justify-center gap-2 disabled:opacity-60">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save reading
          </button>
        </form>
      )}

      {readings.length === 0 ? (
        <div className="bg-white border border-[#E7E5E4] rounded-2xl p-10 text-center" data-testid="tracker-empty">
          <Activity className="w-10 h-10 text-teal/40 mx-auto" />
          <p className="mt-3 text-sm text-[#78716C]">No readings yet. Add your first BIA reading to start tracking progress.</p>
        </div>
      ) : (
        <>
          {/* chart */}
          <div className="bg-white border border-[#E7E5E4] rounded-2xl p-6 shadow-sm">
            <div className="flex flex-wrap gap-2">
              {METRICS.map((m) => (
                <button key={m.id} onClick={() => setMetric(m.id)} data-testid={`metric-${m.id}`}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${metric === m.id ? "bg-teal text-white" : "bg-[#F5F5F4] text-[#57534E] hover:bg-[#EBEAE8]"}`}>
                  {m.label}
                </button>
              ))}
            </div>
            <div className="mt-5 h-64">
              {chartData.length < 2 ? (
                <div className="h-full flex items-center justify-center text-sm text-[#A8A29E]">Add at least two readings with {activeMetric.label} to see a trend line.</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EDECEA" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#78716C" }} />
                    <YAxis domain={["auto", "auto"]} tick={{ fontSize: 11, fill: "#78716C" }} />
                    <Tooltip formatter={(v) => [`${v}${unitLabel}`, activeMetric.label]} />
                    <Line type="monotone" dataKey="value" stroke="#0d9488" strokeWidth={2.5} dot={{ r: 3.5, fill: "#0d9488" }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* history */}
          <div className="bg-white border border-[#E7E5E4] rounded-2xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="tracker-history">
                <thead>
                  <tr className="bg-[#F5F5F4] text-left text-[11px] uppercase tracking-wider text-[#78716C]">
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Weight</th>
                    <th className="px-4 py-3">Body fat %</th>
                    <th className="px-4 py-3">Visceral</th>
                    <th className="px-4 py-3">LBM</th>
                    <th className="px-4 py-3">Water %</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {[...readings].reverse().map((r) => (
                    <tr key={r.id} className="border-t border-[#F5F5F4]" data-testid={`reading-${r.id}`}>
                      <td className="px-4 py-3 text-navy font-medium whitespace-nowrap">{r.date}</td>
                      <td className="px-4 py-3">{fmtMass(r.weight_kg, unit)}</td>
                      <td className="px-4 py-3">{fmtVal(r.body_fat_pct, "%")}</td>
                      <td className="px-4 py-3">{fmtVal(r.visceral_fat)}</td>
                      <td className="px-4 py-3">{fmtMass(r.lbm_kg, unit)}</td>
                      <td className="px-4 py-3">{fmtVal(r.water_pct, "%")}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => del(r)} data-testid={`delete-reading-${r.id}`} className="text-[#A8A29E] hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
